import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  useQuery,
} from '@tanstack/react-query';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import * as zod from "zod";
import * as zVal from '../lib/validations';
import { getFindAProviderInitialData, postFindAProviderListData, getProviderData, postFindAProviderPrint } from '../lib/api';
import '../App.css';
import { Search, ChevronLeft, Download } from 'lucide-react';
import DoctorDetails from '../components/fin-a-provider/doctor-details'
import HospitalDetails from '../components/fin-a-provider/hospital-details'
import PharmacyDetails from '../components/fin-a-provider/pharmacy-details'
import Text from '../components/ui/text';
import Select from '../components/ui/select';
import Autocomplete from '../components/ui/autocomplete';
import Button from '../components/ui/button';
import RadioGroup from '../components/ui/radio-group';
import Input from '../components/ui/input';
import Switch from '../components/ui/switch';
import Loader from '../components/ui/loader';
import { Tooltip } from "@nextui-org/react";
import ProviderList from '../components/fin-a-provider/providers-list';
import { useTranslation } from 'react-i18next';
import { getUrlProviderType } from '../lib/utils';
import DownloadPdfIconSrc from '../assets/images/pdf-icon.svg';
import { captureError } from '../lib/sentry';

const formSchema = zod
  .object({
    Search: zVal.textNullableOptional(),
    /* providerType: zod.preprocess(
      (val) => parseInt(val, 10), 
      zod.number({
        required_error: "Required",
        invalid_type_error: "Required",
      })
      .int()
      .positive()
      .nullable()
      .optional()
    ), */
    ProviderTypeId: zVal.textRequired(),
    ProviderSpecialty: zVal.textNullableOptional(),
    CountyName: zVal.textNullableOptional(),
    Radius: zVal.selectWithNumberValueNullableOptional(),
    ZipCode: zVal.zipCodeOptionalEmpty(),
    advancedSearch: zVal.booleanNullableOptional(),
    Gender: zVal.textNullableOptional(),
    ProviderAcceptsNewPatient: zVal.textNullableOptional(),
    Language: zVal.textNullableOptional(),
    HasBoardCertifications: zod.preprocess(
      (val) => (val === null ? val : (val === 'true')), 
      zod.boolean().nullable().optional(),
    ),
    HospitalId: zVal.textNullableOptional(),
    PayeeId: zVal.textNullableOptional(),
    HasAccreditation: zod.preprocess(
      (val) => (val === null ? val : (val === 'true')), 
      zod.boolean().nullable().optional(),
    ),  
  })
  .superRefine((data, ctx) => {
    if (
      data?.ProviderTypeId === '2' &&
      (data?.ProviderSpecialty === null || data?.ProviderSpecialty === undefined || data?.ProviderSpecialty === '')
    ) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['ProviderSpecialty'],
        message: 'Required',
      });
    }
  });
const resolver = zodResolver(formSchema);

const distanceList = [
  { value: 0, label: 'All' },
  { value: 1, label: '1 mile' },
  { value: 5, label: '5 miles' },
  { value: 10, label: '10 miles' },
  { value: 20, label: '20 miles' },
  { value: 50, label: '50 miles' },
];

const genderList = [
  { value: 'F', label: 'Female' },
  { value: 'M', label: 'Male' },
];

const acceptNewPatientList = [
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
];

const boardCertificationList = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const accreditationsList = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const pageSize = 12;

function App() {
  const { t } = useTranslation();
  const contentRef = useRef();
  const [requestParams, setRequestParams] = useState(null);
  const [searchType, setSearchType] = useState('distance');
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(null);
  const [providerId, setProviderId] = useState(null);
  const [providers, setProviders] = useState();
  const [providersErrors, setProvidersErrors] = useState([]);
  const [provider, setProvider] = useState();
  const [printList, setPrintList] = useState(false);
  const [showSearchTooltip, setShowSearchTooltip] = useState(false);

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      "Gender": null,
      "Language": null,
      "Search": null,
      "ProviderTypeId": getUrlProviderType(),
      "ProviderSpecialty": null,
      "Radius": '0',
      "ZipCode": '',
      "HasBoardCertifications": null,
      "HasAccreditation": null,
      "HospitalId": null,
      "PayeeId": null,
      "ProviderAcceptsNewPatient": null
    },
    resolver
  });

  // Form Validation
  const {
    reset,
    resetField,
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid }
  } = methods;

  // Request Initial Data
  const { isPending: initialDataIsPending, isError: initialDataIsError, data: initialData, error: initialDataError } = useQuery({
    queryKey: ['findAProviderInitialData'],
    queryFn: async () => {
      try {
        const response = await getFindAProviderInitialData();
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'find-a-provider', query: 'findAProviderInitialData' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
  });

  // Request List Data
  const { isPending: listDataIsPending, isError: listDataIsError, data: listData, error: listDataError  } = useQuery({
    queryKey: ['findAProviderList', requestParams],
    queryFn: async () => {
      try {
        const response = await postFindAProviderListData(requestParams);
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'find-a-provider', query: 'findAProviderList' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
    enabled: !!requestParams,
  });
  
  // Request Item Data
  const { isPending: providerDataIsPending, isError: providerDataIsError, data: providerData, error: providerDataError  } = useQuery({
    queryKey: ['providerData', providerId],
    queryFn: async () => {
      try {
        const response = await getProviderData(providerId);
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'find-a-provider', query: 'providerData' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
    enabled: !!providerId,
  });

  // Print
  const { isPending: printIsPending, isError: printIsError, data: printData, error: printError  } = useQuery({
    queryKey: ['postFindAProviderPrint', printList, requestParams],
    queryFn: async () => {
      try {
        const response = await postFindAProviderPrint(requestParams);
        setPrintList(false);
        return response;
      } catch (error) {
        // Capturar el error en Sentry
        captureError(error, { component: 'find-a-provider', query: 'postFindAProviderPrint' });
        throw error; // Relanzar el error para que React Query lo maneje
      }
    },
    enabled: !!printList,
  });

  const watchProviderTypeId = watch('ProviderTypeId');

  /* useEffect(() => {
    if (initialData) {
      const urlProviderType = getUrlProviderType();
      console.log('urlProviderType', urlProviderType);
      if (urlProviderType) {
        setValue('ProviderTypeId', urlProviderType);
      }
    }
  }, [initialData, setValue]); */

  useEffect(() => {
    setTotal(listData?.data?.totalCount ? Math.ceil(listData.data.totalCount / pageSize) : 0);
    if (listData?.data?.items) {
      setProviders(listData.data.items);
      setProviderId(null);
    }
    if (listData?.errors && listData?.errors?.length > 0) {
      setProvidersErrors(listData?.errors);
    } else {
      setProvidersErrors([]);
    }
  }, [listData]);

  useEffect(() => {
    if (providerData?.data) {
      setProvider(providerData?.data);
    }
  }, [providerData]);

  useEffect(() => {
    if (isValid) {
      setRequestParams({ ...requestParams, PageNumber: page });
    }
  }, [page]);

 /*  useLayoutEffect(() => {
    if (listData || providerData) {
      window.location.hash='content';
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  }, [listData, providerData]); */

  useEffect(() => {

    if (watchProviderTypeId) resetField('ProviderSpecialty');

  }, [watchProviderTypeId]);

  // Select Lists
  const providerTypelist = useMemo(() => initialData?.data?.providerTypelist?.map(i => ({ label: i.name, value: i.id})), [initialData]);
  const specialityList = useMemo(() => {
    const filtered = initialData?.data?.specialtyList?.filter(i => i.providerTypeId === parseInt(watchProviderTypeId))[0];
    return filtered?.list?.map(i => ({ label: i.name, value: i.id}));
  }, [initialData, watchProviderTypeId]);  
  const defaultSpecialityKey = useMemo(() => specialityList?.[0]?.value?.toString() || null, [specialityList]);
  const countyList = useMemo(() => initialData?.data?.countyList?.map(i => ({ label: i.name, value: i.id})), [initialData]);
  const languageList = useMemo(() => initialData?.data?.languageList?.map(i => ({ label: i.name, value: i.id})), [initialData]);
  const hospitalList = useMemo(() => initialData?.data?.hospitalList?.map(i => ({ label: i.name, value: i.id})), [initialData]);
  const groupAfiliationList = useMemo(() => {
    const providerType = watchProviderTypeId ? watchProviderTypeId : "1";
    let index = null;
    switch (providerType) {
      case "1":
        index = 'pcpPayeeList';
        break;
      case "2":
        index = 'spcialtyList';
        break;
      default:
        index = null;
    }
    if (index) {
      return initialData?.data?.groupAfiliation?.[index]?.map(i => ({ label: i.name, value: i.id}));
    } else {
      return [];
    }
  }, [initialData, watchProviderTypeId]);

  const onSubmit = data => {
    const paramsWithValue = Object.fromEntries(Object.entries(data).filter(([_, v]) => v != null));
    setRequestParams({
      ...paramsWithValue,
      refresh: Math.random(),
      PageNumber: 1,
      PageSize: pageSize,
      SearchByDistanceOrCounty: searchType,
    }); 
    setPage(1);
  }

  const onResetSearch = useCallback(() => {
    reset();
    setRequestParams(null);
    setPage(1);
  }, [reset, setRequestParams, setPage]);

  return (
    <div className='component-container font-monserrat w-full h-auto flex flex-col justify-start items-start font-normal'>      
      <div className='w-full h-auto flex flex-col gap-10 relative'>
        {initialDataIsPending && <Loader />}
        <div className='flex flex-col gap-5 pb-12 border-b border-gray-600'>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
              <div className='flex flex-col sm:flex-row justify-start gap-10'>
                <div className='w-auto flex flex-col justify-center items-start'>
                  <RadioGroup
                    name='searchType'
                    size='sm'
                    value={searchType}
                    options={[
                      { label: t('Search by Distance'), value: 'distance' },
                      { label: t('Search by County'), value: 'county' }
                    ]} 
                    onValueChange={setSearchType}                 
                  />
                </div>
                
                <div className='col-span-1 sm:col-span-6 flex flex-1 flex-col justify-start items-start rounded-full'>
                  <Text type='label' htmlFor='ProviderTypeId'>Provider Type</Text>
                  <Select
                    {...register('ProviderTypeId')}
                    size='sm'
                    label={<Text>Select</Text>}
                    options={providerTypelist}
                    defaultSelectedKeys={[getUrlProviderType()]}
                    selectionMode="single"
                    disallowEmptySelection={true}
                    errorMessage={errors.ProviderTypeId?.message || null}
                    isInvalid={errors.ProviderTypeId ? true : false}
                  />
                </div>
                    
                <div className='w-full flex-1 flex flex-row justify-center sm:justify-end'>
                  <div className='w-full'>
                    <Text type='label' htmlFor='ProviderSpecialty'>Specialty</Text>
                    <Select
                      key={`speciality-${watchProviderTypeId}`}
                      {...register('ProviderSpecialty')}
                      size='sm'
                      label={<Text>Select a specialty</Text>}
                      options={specialityList}
                      selectionMode="single"
                      defaultSelectedKeys={[' ALL']} /* TODO: Add default selected key */
                      isDisabled={specialityList?.length === 0}
                      errorMessage={errors?.ProviderSpecialty?.message || null}
                      isInvalid={errors?.ProviderSpecialty ? true : false}
                    />
                  </div>
                </div>
              </div>
              <div className='flex flex-col sm:flex-row gap-5'>
                {(['1','2'].includes(watchProviderTypeId)) && (<div className='flex-1 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='PayeeId'>Medical Center Group</Text>
                  <Autocomplete
                    name='PayeeId'
                    control={control}
                    size='sm'
                    label={<Text>Medical Center Group</Text>}
                    options={groupAfiliationList}
                    selectionMode="single"
                    isRequired={false}
                    errorMessage={errors.PayeeId?.message || null}
                    isInvalid={errors.PayeeId ? true : false}
                    radius='full'
                    classNames={{
                      label: 'text-sm font-normal'
                    }}
                  />
                </div>)}
                <div className='flex-1 flex flex-col justify-start items-start'>
                    <Tooltip content={<Text>Search providers by name, phone, or location.</Text>} isOpen={showSearchTooltip} color='default' placement='bottom-end' showArrow>
                      <div className='w-full'>
                        <Text type='label' htmlFor='Search'>Search</Text>
                        <Input 
                          {...register('Search')} 
                          size='sm'
                          /* classNames={{
                            inputWrapper: 'h-8 text-sm'
                          }} */
                          placeholder={t('Search Name, City or Phone...')}
                          isClearable
                          startContent={
                            <Search />
                          }
                          onFocus={() => setShowSearchTooltip(true)}
                          onBlur={() => setShowSearchTooltip(false)}
                        />
                      </div>
                    </Tooltip>
                </div>
              </div>
              <div className='flex flex-row gap-5'>
                {searchType === 'distance' && (
                  <>
                    <div className='flex-1 flex flex-col justify-start items-start'>
                      <Text type='label' htmlFor='ZipCode'>Zip Code</Text>
                      <Input 
                        {...register('ZipCode')} 
                        label={<Text>Zip Code</Text>}
                        size='sm'
                        isRequired={false}
                        errorMessage={errors.ZipCode?.message || null}
                        isInvalid={errors.ZipCode ? true : false}
                      />
                    </div>
                    <div className='flex-1 flex flex-col justify-start items-start'>
                      <Text type='label' htmlFor='Radius'>Distance</Text>
                      <Select
                        {...register('Radius')}
                        size='sm'
                        label={<Text>Select</Text>}
                        options={distanceList}
                        selectionMode="single"
                        disallowEmptySelection={true}
                        defaultSelectedKeys={[distanceList[0].value.toString()]}
                        errorMessage={errors.Radius?.message || null}
                        isInvalid={errors.Radius ? true : false}
                      />
                    </div>
                  </>
                )}
                {searchType === 'county' && (
                  <div className='flex-1 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='CountyName'>County</Text>
                    <Select
                      {...register('CountyName')}
                      size='sm'
                      label={<Text>Select a county</Text>}
                      options={countyList}
                      selectionMode="single"
                      errorMessage={errors.CountyName?.message || null}
                      isInvalid={errors.CountyName ? true : false}
                    />
                  </div>
                )}
              </div>
              <div className='flex flex-row justify-end items-center'>
                <Switch size='sm' name='advancedSearch' defaultSelected={advancedSearch} onChange={() => setAdvancedSearch(!advancedSearch)} label={<Text>Advanced Search</Text>} />
                <Text type='label' htmlFor='advancedSearch'>Advanced Search</Text>
              </div>
              {advancedSearch && (
                <div className='flex flex-col gap-5'>
                  <div className='grid grid-cols-1 sm:grid-cols-6 md:grid-cols-12 gap-5'>
                    {(['0','1','2','3','7','8'].includes(watchProviderTypeId)) && (
                      <div className='col-span-1 sm:col-span-2 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor='Gender'>Gender</Text>
                        <Select
                          {...register('Gender')}
                          size='sm'
                          label={<Text>Select</Text>}
                          options={genderList}
                          selectionMode="single"
                          isRequired={false}
                          errorMessage={errors.Gender?.message || null}
                          isInvalid={errors.Gender ? true : false}
                        />
                      </div>
                    )}
                    {(['0','1','2','3','7','8'].includes(watchProviderTypeId)) && (
                      <div className='col-span-1 sm:col-span-4 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor='ProviderAcceptsNewPatient'>Do you accept new patients?</Text>
                        <Select
                          {...register('ProviderAcceptsNewPatient')}
                          size='sm'
                          label={<Text>Select</Text>}
                          options={acceptNewPatientList}
                          selectionMode="single"
                          isRequired={false}
                          errorMessage={errors.ProviderAcceptsNewPatient?.message || null}
                          isInvalid={errors.ProviderAcceptsNewPatient ? true : false}
                        />
                      </div>
                    )}
                    {(['0','1','2','3','7','8'].includes(watchProviderTypeId)) && (
                      <div className='col-span-1 sm:col-span-3 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor='Language'>Language</Text>
                        <Select
                          {...register('Language')}
                          size='sm'
                          label={<Text>Select</Text>}
                          options={languageList}
                          selectionMode="single"
                          isRequired={false}
                          errorMessage={errors.Language?.message || null}
                          isInvalid={errors.Language ? true : false}
                        />
                      </div>
                    )}
                    {(['0','1','2','3','7','8'].includes(watchProviderTypeId)) && (
                      <div className='col-span-1 sm:col-span-3 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor='HasBoardCertifications'>Board Certification</Text>
                        <Select
                          {...register('HasBoardCertifications')}
                          size='sm'
                          label={<Text>Select</Text>}
                          options={boardCertificationList}
                          selectionMode="single"
                          isRequired={false}
                          errorMessage={errors.HasBoardCertifications?.message || null}
                          isInvalid={errors.HasBoardCertifications ? true : false}
                        />
                      </div>
                    )}
                    {(['0','1','2','3','7','8'].includes(watchProviderTypeId)) && (
                      <div className='col-span-1 sm:col-span-6 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor='HospitalId'>Hospital Affiliation</Text>
                        <Autocomplete
                          name='HospitalId'
                          control={control}
                          size='sm'
                          label={<Text>Select</Text>}
                          options={hospitalList}
                          selectionMode="single"
                          isRequired={false}
                          errorMessage={errors.HospitalId?.message || null}
                          isInvalid={errors.HospitalId ? true : false}
                        />
                      </div>
                    )}          
                    {(['4','5','6','9'].includes(watchProviderTypeId)) && (
                      <div className='col-span-1 sm:col-span-6 flex flex-col justify-start items-start'>
                        <Text type='label' htmlFor='HasAccreditation'>Accreditations</Text>
                        <Select
                          {...register('HasAccreditation')}
                          size='sm'
                          label={<Text>Select</Text>}
                          options={accreditationsList}
                          selectionMode="single"
                          isRequired={false}
                          errorMessage={errors.HasAccreditation?.message || null}
                          isInvalid={errors.HasAccreditation ? true : false}
                        />
                      </div>
                    )}
                  </div>      
                </div>
              )}
              
              <div className='flex flex-row justify-center items-center'>
                <Tooltip content={<Text>No results found.</Text>} isOpen={providers?.length === 0} color='secondary' placement='bottom' showArrow>
                  <div className='w-full flex flex-row justify-center items-center gap-3'>
                    <div>
                      <Button type='submit' color='primary' radius='full' isDisabled={requestParams && listDataIsPending} isLoading={requestParams && listDataIsPending}>
                        <Text>Search</Text>
                      </Button>
                    </div>
                    <div>
                      <Button onClick={onResetSearch} size='sm' radius='full' variant='secondary'>
                        <Text>Reset Search</Text>
                      </Button>
                    </div>
                    
                  </div>
                </Tooltip>
              </div>
            </form>
          </FormProvider>
        </div>
        <div id='content' ref={contentRef} className='relative mb-10'>
          {((listDataIsPending && requestParams) || (providerDataIsPending && providerId)) && <Loader />}
          {(providerId && !providerDataIsPending) && (
            <div className='flex flex-col'>
              <div className='w-full flex flex-row justify-between border-b border-gray-300 pb-5'>
                <div className='flex flex-row justify-left items-center gap-5'>
                  <Button isIconOnly color='primary' radius="full" className='h-10 min-w-fit' onClick={() => setProviderId(null)}><ChevronLeft /></Button>
                  <div className='uppercase text-gray-600 text-lg font-normal'>{provider?.providerType}</div>
                </div>
              </div>
              <div className='mt-10'>
                {[1,2,3,7,8].includes(provider?.providerTypeId) && (<DoctorDetails data={provider} />)}
                {[4,6,9].includes(provider?.providerTypeId) && (<HospitalDetails data={provider} />)}
                {provider?.providerTypeId === 5 && (<PharmacyDetails data={provider} />)}
              </div>
            </div>
          )}
          {((providerId && providerDataIsPending) || !providerId) && (<ProviderList requestParams={requestParams} listDataIsPending={listDataIsPending} providers={providers} total={total} page={page} setPage={setPage} setProviderId={setProviderId} errors={providersErrors} onResetSearch={onResetSearch} />)}
        </div>
        {providers?.length > 0 && !listDataIsPending && (
          <div className=''>
              <div className='p-5 border-b border-gray-500 flex flex-row justify-between items-center'>
                <div className='flex flex-row justify-start items-center gap-3'>
                  <img src={DownloadPdfIconSrc} />
                  <Text type='h5'>List of Providers</Text>
                </div>
                <div>
                  <Button size='sm' onClick={() => setPrintList(true)} radius='full' isLoading={printList} className='bg-gray-100 text-gray-600 text-base w-10 p-0 m-0 sm:w-full sm:min-w-36'>
                    {!printList && <Download size={18} />}
                    <Text className='hidden sm:block'>Download</Text>
                  </Button>
                </div>
              </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default App
